import React, {useState, useEffect, useCallback} from 'react';
import {Avatar, Dropdown, Popover, Spin} from 'antd';
import {UserRoundCog, MessageCircleMore, LogOut, ClipboardList, ChevronDown, Ghost, VenetianMask} from "lucide-react";
import {UserOutlined} from '@ant-design/icons';
import {useDispatch} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import {logout} from '../../store/auth/user.action';
import SearchComponent from '../Search/Search';
import {APIv1} from "../../api";
import Localization from "../Localizations/Localization";
import {useTranslation} from "react-i18next";
import UserRoleMask from "../UserRoleMask/UserRoleMask";
import HeaderNotification from "../HeaderNotification/HeaderNotification";
import VpsBalance from "../VpsBalance/VpsBalance";
import useUserMaskStore from "../../store/user/userMaskStore";
import {getStoredUser, USER_STORAGE_UPDATED_EVENT} from "../../utils";
import './Header.scss';

const Header = ({isCollapse, isScrolled, searchText, setSearchText}) => {
    const {t} = useTranslation();
    const [isUserOptions, setIsUserOptions] = useState(false);
    const [userAvatar, setUserAvatar] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(() => getStoredUser() || {});
    const isStaff = Boolean(userData?.data?.is_admin);
    const canViewVpsBalance = userData?.data?.is_head === true;
    const maskedCompany = useUserMaskStore((state) => state.maskedCompany);
    const maskLabel = t("pages.orders.listColumns.maskUser");

    const buildAuthHeaders = useCallback(() => {
        const headers = {};

        if (userData.token) {
            headers.Authorization = `Token ${userData.token}`;
        }

        return headers;
    }, [userData.token]);

    const fetchUserAvatar = useCallback(async () => {
        try {
            const storedUser = getStoredUser();
            const companyInn = storedUser?.company_inn || userData.company_inn;
            const headers = {
                ...buildAuthHeaders(),
            };

            if (companyInn) {
                headers['X-User-Company-INN'] = companyInn;
            }

            const userAvatarResponse = await APIv1.get('/company/profile/avatar/', {
                headers
            });
            setUserAvatar(userAvatarResponse.data.data.billing_logo);
        } catch (err) {
            console.error('Something went wrong:', err);
        }
    }, [buildAuthHeaders, userData.company_inn]);

    useEffect(() => {
        const refreshUserData = () => {
            const items = getStoredUser();

            if (items) {
                setUserData(items);
            }
        };

        refreshUserData();
        window.addEventListener(USER_STORAGE_UPDATED_EVENT, refreshUserData);
        window.addEventListener('storage', refreshUserData);

        return () => {
            window.removeEventListener(USER_STORAGE_UPDATED_EVENT, refreshUserData);
            window.removeEventListener('storage', refreshUserData);
        };
    }, []);


    useEffect(() => {
        fetchUserAvatar();
    }, [fetchUserAvatar]);

    const handleLogout = () => {
        dispatch(logout());
        navigate("/", {replace: true});
    };

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserRoundCog size={16} color="#6cb4fc"/>,
            label: <Link to="/profile">{t('header.userMenuBar.column1')}</Link>,
        },
        {
            key: 'feedback',
            icon: <MessageCircleMore size={16} color="#ac9cfc"/>,
            label: <Link to="/feedback">{t('header.userMenuBar.column2')}</Link>,
        },
        ...(isStaff ? [{
            key: 'audit-logs',
            icon: <ClipboardList size={16} color="#6cb4fc"/>,
            label: <Link to="/audit-logs">{t('header.userMenuBar.column4')}</Link>,
        }] : []),
        {type: 'divider'},
        {
            key: 'logout',
            icon: <LogOut size={16}/>,
            label: t('header.userMenuBar.column3'),
            danger: true,
        },
    ];

    const handleUserMenuClick = ({key}) => {
        if (key === 'logout') {
            handleLogout();
        }
    };

    if (!userData.token) {
        return <Spin size="large"/>;
    }

    return (
        <section className={`header${isCollapse ? ' header--collapsed' : ''}${isScrolled ? ' header--scrolled' : ''}${isStaff ? ' header--staff' : ''}`}>
            <div className="header__left">
                {canViewVpsBalance && (
                    <div className="header-pill header-pill--vps">
                        <VpsBalance userData={userData}/>
                    </div>
                )}
            </div>

            <div className="header__center">
                <SearchComponent
                    t={t}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    fieldClassName="header-pill header-pill--search"
                    buttonClassName="header-pill header-pill--search-button"
                />
            </div>

            <div className="header__right">
                {isStaff && (
                    <Popover
                        content={
                            <div className="header-mask-popover">
                                <UserRoleMask userData={userData} placeholder={maskLabel}/>
                            </div>
                        }
                        trigger="click"
                        placement="bottomRight"
                    >
                        <div className={`header-pill header-pill--mask${maskedCompany ? ' header-pill--masked' : ''}`}>
                            <button
                                type="button"
                                className="header-mask-button"
                                aria-label={maskLabel}
                                title={maskedCompany ? maskedCompany.name : maskLabel}
                            >
                                {maskedCompany ? <Ghost size={18}/> : <VenetianMask size={18}/>}
                                {maskedCompany && (
                                    <span className="header-mask-button__name">{maskedCompany.name}</span>
                                )}
                            </button>
                        </div>
                    </Popover>
                )}
                <div className="header-pill header-pill--locale">
                    <Localization/>
                </div>
                <div className="header-pill header-pill--notification">
                    <HeaderNotification/>
                </div>
                <div className="header-pill header-pill--user">
                    <Dropdown
                        menu={{items: userMenuItems, onClick: handleUserMenuClick}}
                        trigger={['click']}
                        placement="bottomRight"
                        open={isUserOptions}
                        onOpenChange={setIsUserOptions}
                        rootClassName="header-user-dropdown"
                    >
                        <button
                            type="button"
                            className={`header-user${isUserOptions ? ' header-user--open' : ''}`}
                            aria-label={t('header.userMenuBar.column1')}
                        >
                            <Avatar
                                size={32}
                                src={userAvatar || undefined}
                                icon={!userAvatar && <UserOutlined/>}
                                className="user_avatar"
                            />
                            <span className="header_user_data">
                                <span>{userData.data.username}</span>
                                <span>{isStaff ? 'Admin' : 'Client'}</span>
                            </span>
                            <ChevronDown size={16} className="header-user__chevron"/>
                        </button>
                    </Dropdown>
                </div>
            </div>
        </section>
    );
};

export default Header;
